import { EditOutlined } from '@ant-design/icons';
import { TextField, useForm } from '@refinedev/antd';
import { Button, Card, Flex, Form, Skeleton } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { SERVICE_ORDER_STATUS, ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';

import ServiceOrderAdminData from '../ServiceOrderForm/ServiceOrderAdminData';

interface ServiceOrderShowAdminProps {
  serviceOrder: ServiceOrder;
  refetch?: () => Promise<any>;
  modifingAdminData?: boolean;
  setModifingAdminData?: (modifying: boolean) => void;
}

const ServiceOrderShowAdmin = (props: ServiceOrderShowAdminProps) => {
  const { userWorkArea } = useUserContext();
  const { t } = useTranslation();
  const { serviceOrder, refetch, modifingAdminData, setModifingAdminData } = props;
  const { kilometres_to_pay, kilometres_rate, hourly_rate, hours_to_pay, currency } = serviceOrder;
  const [saveEdit, setSaveEdit] = useState<boolean>(false);
  const formatWithSuffix = (value: number | string | null | undefined, suffix: string) => {
    if (value === undefined || value === null || value === '') {
      return '-';
    }

    return `${value} ${suffix}`;
  };

  const { formProps } = useForm({
    redirect: false,
    action: 'edit',
    resource: 'service_orders',
    id: serviceOrder.id,
    queryOptions: { enabled: false },
    successNotification: {
      message: t('Orden de servicio actualizada correctamente'),
      type: 'success',
    },
    onMutationSuccess: () => {
      void refetch?.().then(() => {
        setSaveEdit(false);
      });
    },
    onMutationError: () => setSaveEdit(false),
  });

  useEffect(() => {
    // Mantiene el form sincronizado con la última versión persistida.
    if (!modifingAdminData) {
      formProps?.form?.setFieldsValue(serviceOrder);
    }
  }, [formProps?.form, modifingAdminData, serviceOrder]);

  return (
    <Card className="ServiceOrderShowSection ServiceOrderShowAdmin">
      <div className="ServiceOrderShowTitle">{t('Detalles de administración')}</div>
      {modifingAdminData ? (
        <Form
          {...formProps}
          initialValues={serviceOrder}
          onFinish={v => {
            setSaveEdit(true);
            formProps?.onFinish?.(v);
            setModifingAdminData?.(false);
          }}
          layout="vertical">
          <ServiceOrderAdminData formProps={formProps} />
          <Flex justify="end" align="flex-end">
            <Button onClick={() => setModifingAdminData?.(false)} style={{ marginRight: 8 }}>
              {t('Cancelar')}
            </Button>
            <Button htmlType="submit" type="primary">
              {t('Guardar')}
            </Button>
          </Flex>
        </Form>
      ) : (
        <div className="ServiceOrderShowDataCard">
          <div className="ServiceOrderShowData">
            <b>{t('Horas informadas por técnico')}</b>
            <div>
              <TextField value={formatWithSuffix(serviceOrder.hours, 'hs')} />
            </div>
          </div>
          <div className="ServiceOrderShowData">
            <b>{t('Kilómetros informados por técnico')}</b>
            <div>
              <TextField value={formatWithSuffix(serviceOrder.kilometres, 'km')} />
            </div>
          </div>
          <div className="ServiceOrderShowData">
            <b>{t('Horas a pagar')}</b>
            <div>
              <TextField value={formatWithSuffix(serviceOrder.hours_to_pay, 'hs')} />
            </div>
          </div>
          <div className="ServiceOrderShowData">
            <b>{t('Kilómetros a pagar')}</b>
            <div>
              <TextField value={formatWithSuffix(serviceOrder.kilometres_to_pay, 'km')} />
            </div>
          </div>
        </div>
      )}
      {saveEdit && <Skeleton active />}
      {!modifingAdminData && !saveEdit && (
        <>
          <div className="ServiceOrderShowData" style={{ marginTop: 16 }}>
            <b>
              {t('TOTAL POR HORAS Y KILÓMETROS')} ({t('sin IVA')})
            </b>
            <div>
              <TextField
                value={
                  kilometres_to_pay !== undefined &&
                  kilometres_rate !== undefined &&
                  hours_to_pay !== undefined &&
                  hourly_rate !== undefined &&
                  currency !== undefined
                    ? formatWithSuffix(
                        kilometres_to_pay * kilometres_rate + hours_to_pay * hourly_rate,
                        currency?.simbolo,
                      )
                    : '-'
                }
              />
            </div>
          </div>
          <Flex justify="space-between" style={{ marginTop: 16 }} align="end">
            <div className="ServiceOrderShowData">
              <b>{t('Números de factura')}</b>
              <ul>
                {serviceOrder.billing_numbers
                  ?.split(',')
                  .map((number, index) => <li key={index}>{number.trim()}</li>) || '-'}
              </ul>
            </div>
            {serviceOrder.status != SERVICE_ORDER_STATUS.FINALIZADA &&
              (userWorkArea === USER_WORK_AREA.ADMINISTRATION ||
                userWorkArea === USER_WORK_AREA.SOFTWARE) && (
                <Button
                  icon={<EditOutlined />}
                  onClick={() => {
                    formProps?.form?.setFieldsValue(serviceOrder);
                    setModifingAdminData?.(true);
                  }}>
                  {t('Editar')}
                </Button>
              )}
          </Flex>
        </>
      )}
    </Card>
  );
};

export default ServiceOrderShowAdmin;
